<template>
  <div class="advanced-search">
    <!-- 保存的查询条件 -->
    <div class="saved-queries-section">
      <div class="saved-queries-header">
        <span>保存的查询</span>
        <el-button type="text" size="mini" style="padding-left: 5px;padding-right: 5px;" class="custom-primary-btn" plain @click="openSaveDialog">
          <i class="el-icon-plus"></i> 保存当前查询
        </el-button>
      </div>
      <div class="saved-queries-list" v-if="savedQueries.length > 0 && !animationLoading">
        <el-tag
            v-for="query in savedQueries"
            :key="query.id"
            closable
            @close="deleteSavedQuery(query)"
            @click="applySavedQuery(query)"
            class="query-tag"
            :type="currentQueryId === query.id ? 'primary' : ''"
        >
          {{ query.selectName }}
        </el-tag>
      </div>
      <!-- 加载动画 -->
      <div class="loaderAnimation" v-if="animationLoading">
        <span class="loader-text">loading</span>
        <span class="load"></span>
      </div>

    </div>

    <!-- 查询条件行 -->
    <div class="search-conditions" v-for="(condition, index) in conditions" :key="index">
      <div class="condition-row">
        <!-- 左侧：查询参数选择 -->
        <el-select
            v-model="condition.prop"
            placeholder="请选择查询参数"
            @change="onFieldChange(index, condition.prop)"
            class="field-select w-full"
            size="mini"
            clearable
            filterable
        >
          <el-option
              v-for="field in availableFields"
              :key="field.prop"
              :label="field.title || field.label"
              :value="field.prop"
          >
          </el-option>
        </el-select>

        <!-- 中间：运算符选择 -->
        <el-select
            v-model="condition.operator"
            placeholder="运算符"
            @change="onOperatorChange(index, condition.prop)"
            class="operator-select w-full"
            size="mini"
        >
          <el-option
              v-for="op in operatorOptions"
              :key="op.value"
              :label="op.label"
              :value="op.value"
              :disabled="op.disabled"
          >
          </el-option>
        </el-select>

        <!-- 右侧：值输入组件 -->
        <div class="value-input">
          <!-- 文本输入框 -->
          <el-input
              v-if="getSearchType(condition.prop) === 'input'"
              v-model="condition.value"
              placeholder="请输入"
              size="mini"
              clearable
          ></el-input>

          <!-- 数字输入框 -->
          <el-input-number
              v-else-if="getSearchType(condition.prop) === 'number'"
              v-model="condition.value"
              :placeholder="getPlaceholder(condition.prop)"
              controls-position="right"
              size="mini"
              class="w-full"
          ></el-input-number>

          <!-- 下拉选择框 -->
          <el-select
              v-else-if="getSearchType(condition.prop) === 'selectopt' || getSearchType(condition.prop) === 'selectmoreopt'"
              v-model="condition.value"
              placeholder="请选择"
              size="mini"
              clearable
              filterable
              :multiple="condition.operator === 'between'"
              class="w-full"
          >
            <el-option
                v-for="option in getFieldOptions(condition.prop)"
                :key="option.value"
                :label="option.label"
                :value="option.value"
            >
            </el-option>
          </el-select>

          <!-- 字典下拉选择框 -->
          <el-select
              v-else-if="getSearchType(condition.prop) === 'selectdict' || getSearchType(condition.prop) === 'selectmoredict'"
              v-model="condition.value"
              placeholder="请选择"
              size="mini"
              clearable
              filterable
              :multiple="condition.operator === 'between'"
              class="w-full"
          >
            <el-option
                v-for="option in getDictOptions(condition.prop)"
                :key="option.value"
                :label="option.label"
                :value="option.value"
            >
            </el-option>
          </el-select>

          <!-- 日期范围选择器 -->
          <el-date-picker
              v-else-if="['daterange', 'datetimerange', 'datetimeclear'].includes(getSearchType(condition.prop))"
              v-model="condition.value"
              size="mini"
              :type="getSearchType(condition.prop) === 'datetimerange' ? 'datetimerange' : 'daterange'"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              :value-format="getSearchType(condition.prop) === 'datetimerange' ? 'yyyy-MM-dd HH:mm:ss' : 'yyyy-MM-dd'"
              :format="getSearchType(condition.prop) === 'datetimerange' ? 'yyyy-MM-dd HH:mm:ss' : 'yyyy-MM-dd'"
              clearable
              class="w-full"
              :unlink-panels="true"
              :picker-options="pickerOptions"
          >
          </el-date-picker>

          <!-- 日期选择器 -->
          <el-date-picker
              v-else-if="getSearchType(condition.prop) === 'date'"
              v-model="condition.value"
              type="date"
              :placeholder="getPlaceholder(condition.prop)"
              value-format="yyyy-MM-dd"
              format="yyyy-MM-dd"
              size="mini"
              class="w-full"
          >
          </el-date-picker>

          <!-- 日期时间选择器 -->
          <el-date-picker
              v-else-if="['datetime'].includes(getSearchType(condition.prop))"
              v-model="condition.value"
              type="datetime"
              :placeholder="getPlaceholder(condition.prop)"
              value-format="yyyy-MM-dd HH:mm:ss"
              format="yyyy-MM-dd HH:mm:ss"
              size="mini"
              clearable
              class="w-full"
          >
          </el-date-picker>

          <!-- 时间选择器 -->
          <el-time-picker
              v-else-if="getSearchType(condition.prop) === 'time'"
              v-model="condition.value"
              :placeholder="getPlaceholder(condition.prop)"
              value-format="HH:mm:ss"
              format="HH:mm:ss"
              size="mini"
          >
          </el-time-picker>

          <!-- 年份选择器 -->
          <el-date-picker
              v-else-if="getSearchType(condition.prop) === 'year'"
              v-model="condition.value"
              type="year"
              :placeholder="getPlaceholder(condition.prop)"
              value-format="yyyy"
              format="yyyy"
              size="mini"
          >
          </el-date-picker>

          <!-- 月份选择器 -->
          <el-date-picker
              v-else-if="getSearchType(condition.prop) === 'month'"
              v-model="condition.value"
              type="month"
              :placeholder="getPlaceholder(condition.prop)"
              value-format="yyyy-MM"
              format="yyyy-MM"
              size="mini"
          >
          </el-date-picker>

          <!-- 年份范围选择器 -->
          <el-date-picker
              v-else-if="getSearchType(condition.prop) === 'yearrange'"
              v-model="condition.value"
              type="yearrange"
              range-separator="至"
              start-placeholder="开始年份"
              end-placeholder="结束年份"
              value-format="yyyy"
              format="yyyy"
              size="mini"
          >
          </el-date-picker>

          <!-- 数字范围输入 -->
          <div v-else-if="getSearchType(condition.prop) === 'numberrange'" class="number-range-input">
            <el-input-number
                v-model="condition.value[0]"
                placeholder="最小值"
                controls-position="right"
                class="range-input"
                size="mini"
            ></el-input-number>
            <span class="range-separator">至</span>
            <el-input-number
                v-model="condition.value[1]"
                placeholder="最大值"
                controls-position="right"
                class="range-input"
                size="mini"
            ></el-input-number>
          </div>

          <!-- 多选下拉框 -->
          <el-select
              v-else-if="getSearchType(condition.prop) === 'multiselect'"
              v-model="condition.value"
              :placeholder="getPlaceholder(condition.prop)"
              size="mini"
              multiple
              clearable
          >
            <el-option
                v-for="option in getFieldOptions(condition.prop)"
                :key="option.value"
                :label="option.label"
                :value="option.value"
            >
            </el-option>
          </el-select>

          <!-- 开关 -->
          <el-switch
              v-else-if="getSearchType(condition.prop) === 'switch'"
              v-model="condition.value"
              size="mini"
              active-text="是"
              inactive-text="否"
          >
          </el-switch>

          <!-- 单选框 -->
          <el-radio-group
              v-else-if="getSearchType(condition.prop) === 'radio'"
              v-model="condition.value"
          >
            <el-radio
                v-for="option in getFieldOptions(condition.prop)"
                :key="option.value"
                :label="option.value"
            >
              {{ option.label }}
            </el-radio>
          </el-radio-group>

          <!-- 复选框 -->
          <el-checkbox-group
              v-else-if="getSearchType(condition.prop) === 'checkbox'"
              v-model="condition.value"
          >
            <el-checkbox
                v-for="option in getFieldOptions(condition.prop)"
                :key="option.value"
                :label="option.value"
            >
              {{ option.label }}
            </el-checkbox>
          </el-checkbox-group>

          <!-- 默认文本输入框 -->
          <el-input
              v-else
              v-model="condition.value"
              :placeholder="getPlaceholder(condition.prop)"
              size="mini"
              clearable
          ></el-input>
        </div>

        <div style="width: 70px;">
          <!-- 删除按钮 -->
          <el-button
              v-if="conditions.length > 1"
              @click="removeCondition(index)"
              type="danger"
              size="mini"
              icon="el-icon-minus"
              circle
              plain
              class="custom-pink-btn"
          ></el-button>

          <!-- 添加按钮（只在最后一行显示） -->
          <el-button
              v-if="index === conditions.length - 1"
              @click="addCondition"
              type="primary"
              size="mini"
              icon="el-icon-plus"
              circle
              plain
              class="custom-purple-btn"
          ></el-button>
        </div>
      </div>
    </div>

    <!-- 操作按钮 -->
    <div class="action-buttons">
      <el-button @click="handleReset" size="mini" class="custom-pink-btn" plain>
        <i class="el-icon-refresh"></i> 重置
      </el-button>
      <el-button type="primary" size="mini" class="custom-purple-btn" plain @click="handleSearch" :loading="loading">
        <i class="el-icon-search"></i> 查询
      </el-button>
<!--      <el-button type="text" size="mini" @click="toggleAdvanced" v-if="showToggle">-->
<!--        {{ showAllConditions ? '简化' : '高级' }}-->
<!--        <i :class="showAllConditions ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>-->
<!--      </el-button>-->
    </div>

    <!-- 保存查询对话框 -->
    <el-dialog
        custom-class="saveSearchDialog"
        title="保存查询条件"
        :visible.sync="showSaveDialog"
        append-to-body
        :close-on-click-modal="false"
        width="400px"
        @close="resetSaveForm"
    >
      <el-form :model="saveForm" :rules="saveRules" ref="saveForm" label-width="80px">
        <el-form-item label="查询名称" prop="name">
          <el-input
              v-model="saveForm.name"
              placeholder="请输入保存的名称"
              size="small"
              maxlength="20"
              show-word-limit
          ></el-input>
        </el-form-item>
<!--        <el-form-item label="设为默认" prop="isDefault">-->
<!--          <el-switch v-model="saveForm.isDefault"></el-switch>-->
<!--        </el-form-item>-->
<!--        <el-form-item label="共享查询" prop="isShared" v-if="enableShare">-->
<!--          <el-switch v-model="saveForm.isShared"></el-switch>-->
<!--          <span class="tip-text">其他用户可见</span>-->
<!--        </el-form-item>-->
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button size="mini" class="custom-pink-btn" plain @click="showSaveDialog = false">取 消</el-button>
        <el-button type="primary" class="custom-purple-btn" plain size="mini" @click="saveCurrentQuery" :loading="saving">
          保 存
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { validatenull } from '@/tdcommon/validate'
import { getUserSelectList, insertUserSelect, delUserSelect } from '@/api/basic/advancedSearch'

export default {
  name: 'AdvancedSearch',
  props: {
    // 查询字段配置
    fields: {
      type: Array,
      default: () => []
    },
    // 是否显示切换按钮
    showToggle: {
      type: Boolean,
      default: false
    },
    // 初始显示的条件数量
    initialVisibleCount: {
      type: Number,
      default: 3
    },
    // 是否启用共享查询
    enableShare: {
      type: Boolean,
      default: false
    },
    // 默认查询条件
    defaultConditions: {
      type: Array,
      default: () => []
    },
    // 是否启用条件之间的逻辑关系
    enableLogicRelation: {
      type: Boolean,
      default: false
    },
    ModularName: {
      type: String,
      default: () => ''
    }
  },
  data () {
    return {
      conditions: [
        {
          prop: '',
          operator: '',
          value: '',
          searchType: '',
          logic: 'and' // 条件之间的逻辑关系：and/or
        }
      ],
      savedQueries: [],
      currentQueryId: null,
      showSaveDialog: false,
      saveForm: {
        name: '',
        isDefault: false,
        isShared: false
      },
      saveRules: {
        name: [
          { required: true, message: '请输入保存的名称', trigger: 'blur' },
          { min: 2, max: 20, message: '长度在 2 到 20 个字符', trigger: 'blur' }
        ]
      },
      animationLoading: false,
      loading: false,
      saving: false,
      showAllConditions: false,
      // 默认的查询字段配置
      defaultFields: [],
      operatorOptions: [
        { label: '等于', value: 'equalTo', disabled: false },
        { label: '包含', value: 'included', disabled: false },
        { label: '在...中', value: 'between', disabled: false },
        { label: '不等于', value: 'notEqualTo', disabled: false },
        { label: '日期查询', value: 'dateInquiry', disabled: false },
        { label: '大于', value: 'gt', disabled: false },
        { label: '大于等于', value: 'ge', disabled: false },
        { label: '小于', value: 'lt', disabled: false },
        { label: '小于等于', value: 'le', disabled: false },
        { label: '以...开始', value: 'start', disabled: false },
        { label: '以...结束', value: 'end', disabled: false }
      ],
      // searchType 到 ElementUI 组件类型的映射
      searchTypeMap: {
        // 文本输入
        input: 'input',
        text: 'input',
        textarea: 'textarea',
        // 数字输入
        number: 'number',
        numberrange: 'numberrange',
        // 选择框
        selectopt: 'select',
        select: 'select',
        multiselect: 'multiselect',
        // 日期时间
        date: 'date',
        datetime: 'datetime',
        datetimeclear: 'datetime',
        time: 'time',
        year: 'year',
        month: 'month',
        daterange: 'daterange',
        datetimerange: 'datetimerange',
        yearrange: 'yearrange',
        // 其他
        switch: 'switch',
        radio: 'radio',
        checkbox: 'checkbox'
      },
      // 日期组件初始信息
      pickerOptions: {
        shortcuts: [{
          text: '昨天',
          onClick (picker) {
            const end = new Date()
            const start = new Date()
            start.setTime(start.getTime() - 3600 * 1000 * 24)
            picker.$emit('pick', [start, end])
          }
        }, {
          text: '当天',
          onClick (picker) {
            const end = new Date()
            const start = new Date()
            start.setTime(start.getTime())
            picker.$emit('pick', [start, end])
          }
        }, {
          text: '最近三天',
          onClick (picker) {
            const end = new Date()
            const start = new Date()
            start.setTime(start.getTime() - 3600 * 1000 * 24 * 3)
            picker.$emit('pick', [start, end])
          }
        }, {
          text: '最近一周',
          onClick (picker) {
            const end = new Date()
            const start = new Date()
            start.setTime(start.getTime() - 3600 * 1000 * 24 * 7)
            picker.$emit('pick', [start, end])
          }
        }, {
          text: '最近一个月',
          onClick (picker) {
            const end = new Date()
            const start = new Date()
            start.setTime(start.getTime() - 3600 * 1000 * 24 * 30)
            picker.$emit('pick', [start, end])
          }
        }, {
          text: '最近两个月',
          onClick (picker) {
            const end = new Date()
            const start = new Date()
            start.setTime(start.getTime() - 3600 * 1000 * 24 * 60)
            picker.$emit('pick', [start, end])
          }
        }, {
          text: '最近三个月',
          onClick (picker) {
            const end = new Date()
            const start = new Date()
            start.setTime(start.getTime() - 3600 * 1000 * 24 * 90)
            picker.$emit('pick', [start, end])
          }
        }, {
          text: '最近半年',
          onClick (picker) {
            const end = new Date()
            const start = new Date()
            start.setMonth(start.getMonth() - 6)
            picker.$emit('pick', [start, end])
          }
        }, {
          text: '最近一年',
          onClick (picker) {
            const end = new Date()
            const start = new Date()
            start.setMonth(start.getMonth() - 12)
            picker.$emit('pick', [start, end])
          }
        }]
      }
    }
  },
  computed: {
    ...mapState('d2admin/user', [
      'info'
    ]),
    // 合并默认字段和传入的字段
    availableFields () {
      return this.fields.length > 0 ? this.fields.filter(item => item.isShowSearch === '1') : this.defaultFields
    }
    // 运算符映射
    // operatorMap () {
    //   return {
    //     equalTo: { label: '等于', value: 'equalTo' },
    //     notEqualTo: { label: '不等于', value: 'notEqualTo' },
    //     included: { label: '包含', value: 'included' },
    //     notContains: { label: '不包含', value: 'notContains' },
    //     gt: { label: '大于', value: 'gt' },
    //     ge: { label: '大于等于', value: 'ge' },
    //     lt: { label: '小于', value: 'lt' },
    //     le: { label: '小于等于', value: 'le' },
    //     between: { label: '在...之间', value: 'between' },
    //     in: { label: '在列表中', value: 'in' },
    //     notIn: { label: '不在列表中', value: 'notIn' },
    //     start: { label: '以...开始', value: 'start' },
    //     end: { label: '以...结束', value: 'end' }
    //   }
    // }
  },
  created () {
    this.loadSavedQueries()
    // 如果有默认查询条件，则应用它们
    // if (this.defaultConditions && this.defaultConditions.length > 0) {
    //   this.conditions = JSON.parse(JSON.stringify(this.defaultConditions))
    // }
  },
  methods: {
    // 获取字段的searchType
    getSearchType (prop) {
      const field = this.availableFields.find(f => f.prop === prop)
      return field ? field.searchType : 'input'
    },
    // 获取字段配置
    getFieldConfig (prop) {
      return this.availableFields.find(f => f.prop === prop) || {}
    },
    // 获取占位符
    getPlaceholder (prop, type = 'default') {
      const field = this.getFieldConfig(prop)
      if (type === 'start' && field.startPlaceholder) {
        return field.startPlaceholder
      }
      if (type === 'end' && field.endPlaceholder) {
        return field.endPlaceholder
      }
      return field.placeholder || '请输入'
    },
    // 获取字段选项
    getFieldOptions (prop) {
      const field = this.getFieldConfig(prop)
      return JSON.parse(field.editOptions) || field.options || []
    },
    // 获取字段选项
    getDictOptions (prop) {
      const field = this.getFieldConfig(prop)
      return field.editOptions || field.options || []
    },
    // 获取可用的运算符
    // getOperators (prop) {
    //   if (!prop) return []
    //   const field = this.getFieldConfig(prop)
    //   var operatorKeys = field.operators || ['equalTo']
    //
    //   // 根据searchType调整默认运算符
    //   const searchType = this.getSearchType(prop)
    //   if (!field.operators) {
    //     if (['datetimeclear', 'daterange', 'datetimerange', 'yearrange', 'numberrange'].includes(searchType)) {
    //       operatorKeys = ['between']
    //     } else if (['selectopt', 'select', 'radio', 'checkbox'].includes(searchType)) {
    //       operatorKeys = ['equalTo', 'notEqualTo', 'in', 'notIn']
    //     } else if (['number'].includes(searchType)) {
    //       operatorKeys = ['equalTo', 'notEqualTo', 'gt', 'ge', 'lt', 'le']
    //     } else if (['input', 'text'].includes(searchType)) {
    //       operatorKeys = ['equalTo', 'notEqualTo', 'included', 'notContains', 'start', 'end']
    //     }
    //   }
    //
    //   return operatorKeys.map(key => this.operatorMap[key] || { label: key, value: key })
    // },
    // 字段变化时重置运算符和值
    onFieldChange (index, prop) {
      const condition = this.conditions[index]
      const field = this.getFieldConfig(prop)
      condition.searchType = field.searchType

      // 重置运算符为第一个可用运算符
      // condition.operator = 'equalTo'
      // const operators = this.getOperators(prop)
      // if (operators.length > 0) {
      //   condition.operator = operators[0].value
      // } else {
      //   condition.operator = 'equalTo'
      // }

      // 根据searchType重置值
      const searchType = this.getSearchType(prop)
      if (['selectopt', 'selectdict', 'selectmoreopt', 'selectmoredict'].includes(searchType)) {
        condition.operator = 'equalTo' // 等于（oneList）
      } else if (['datetimeclear', 'daterange', 'datetimerange', 'yearrange'].includes(searchType)) {
        condition.operator = 'dateInquiry' // 日期查询（dateList）
      } else {
        condition.operator = 'included' // 包含（inputLike）
      }
      if (['datetimeclear', 'daterange', 'datetimerange', 'yearrange'].includes(searchType)) {
        condition.value = []
      } else if (['multiselect', 'checkbox'].includes(searchType)) {
        condition.value = []
      } else if (searchType === 'numberrange') {
        condition.value = [null, null]
      } else if (searchType === 'switch') {
        condition.value = false
      } else {
        condition.value = ''
      }
    },
    // 运算符变化时重置值
    onOperatorChange (index, prop) {
      const condition = this.conditions[index]
      // const field = this.getFieldConfig(prop)

      // 重置运算符为第一个可用运算符
      // condition.operator = 'equalTo'
      // const operators = this.getOperators(prop)
      // if (operators.length > 0) {
      //   condition.operator = operators[0].value
      // } else {
      //   condition.operator = 'equalTo'
      // }

      // 根据searchType重置值
      const searchType = this.getSearchType(prop)
      if (['selectopt', 'selectdict', 'selectmoreopt', 'selectmoredict'].includes(searchType)) {
        if (condition.operator === 'between') {
          condition.value = validatenull(condition.value) ? [] : condition.value.split(',')
        } else {
          condition.value = validatenull(condition.value) ? '' : condition.value[0].toString()
        }
      }
    },
    // 添加查询条件
    addCondition () {
      this.conditions.push({
        prop: '',
        operator: '',
        value: '',
        searchType: '',
        logic: 'and'
      })
    },
    // 删除查询条件
    removeCondition (index) {
      if (this.conditions.length > 1) {
        this.conditions.splice(index, 1)
      }
    },
    // 处理查询
    async handleSearch () {
      // 验证查询条件
      if (!validatenull(this.conditions.filter(condition => validatenull(condition.prop)))) {
        this.$message.warning('不能查询空条件')
        return
      }
      const invalidConditions = this.conditions.filter(condition => {
        if (!condition.prop || !condition.operator) {
          return false
        }

        const searchType = this.getSearchType(condition.prop)

        // 验证不同类型的值
        if (['datetimeclear', 'daterange', 'datetimerange', 'yearrange'].includes(searchType)) {
          return !condition.value || condition.value.length !== 2
        } else if (searchType === 'numberrange') {
          return !condition.value || condition.value.length !== 2
        } else if (['multiselect', 'checkbox'].includes(searchType)) {
          return !condition.value || condition.value.length === 0
        } else {
          return condition.value === null || condition.value === '' || condition.value === undefined
        }
      })

      if (invalidConditions.length > 0) {
        this.$message.warning('请填写完整的查询条件')
        return
      }

      this.loading = true

      try {
        // 构建查询参数
        const queryParams = this.buildQueryParams()
        // 如果启用清空之前参数，则先清空之前的参数
        await this.$emit('clear-previous-params', this.availableFields)
        // 触发查询事件
        this.$emit('search', queryParams)

        // 保存当前查询到本地存储（临时）
        // this.saveToLocal(queryParams)
      } catch (error) {
        this.$message.error('查询失败：' + error.message)
      } finally {
        this.loading = false
      }
    },
    // 构建查询参数
    buildQueryParams () {
      const params = {
        conditions: [],
        logicRelations: []
      }

      this.conditions.forEach((condition, index) => {
        if (condition.prop && condition.operator &&
            (condition.value !== null && condition.value !== '' &&
                condition.value !== undefined)) {
          const field = this.getFieldConfig(condition.prop)

          const conditionData = {
            prop: condition.prop,
            id: field.id,
            title: field.title || field.label,
            operator: condition.operator,
            value: condition.value,
            searchType: field.searchType,
            label: field.label
          }

          params.conditions.push(conditionData)

          // 添加逻辑关系
          if (this.enableLogicRelation && index > 0) {
            params.logicRelations.push(condition.logic || 'and')
          }
        }
      })

      return params
    },
    // 重置查询条件
    handleReset () {
      this.conditions = [
        {
          prop: '',
          operator: '',
          value: '',
          searchType: '',
          logic: 'and'
        }
      ]
      this.currentQueryId = null
      this.$emit('reset')
    },
    // 切换高级模式
    toggleAdvanced () {
      this.showAllConditions = !this.showAllConditions
    },
    // 保存时 把多选和日期参数值 转为字符串
    transformSearchParams (params) {
      // 定义需要处理的 searchType 类型
      const selectTypes = ['selectopt', 'selectdict', 'selectmoreopt', 'selectmoredict']
      const dateTypes = ['datetimeclear', 'daterange', 'datetimerange']

      // 对每个参数进行处理
      return params.map(param => {
        // 创建参数的副本，避免修改原始数据
        const transformed = { ...param }

        // 检查是否需要转换 select 类型
        if (param.operator === 'between' && selectTypes.includes(param.searchType)) {
          if (Array.isArray(param.value)) {
            // 数组转为字符串，使用逗号分隔
            transformed.value = param.value.join(',')
          }
        }

        // 检查是否需要转换日期类型
        if (dateTypes.includes(param.searchType)) {
          if (Array.isArray(param.value)) {
            // 日期范围转为字符串，使用 ~ 分隔（可根据需求调整分隔符）
            transformed.value = param.value.join('~')
          }
        }

        return transformed
      })
    },
    openSaveDialog () {
      if (validatenull(this.conditions.filter(c => c.prop && c.operator))) {
        this.$message.warning('请选择需要保存的查询条件参数')
        return
      }
      this.showSaveDialog = true
    },
    // 保存当前查询
    async saveCurrentQuery () {
      this.$refs.saveForm.validate(async (valid) => {
        if (valid) {
          this.saving = true

          try {
            const queryData = {
              // id: Date.now().toString(),
              id: null,
              selectName: this.saveForm.name,
              menuId: this.ModularName,
              userId: this.info.username,
              detailsList: this.transformSearchParams(this.conditions),
              timestamp: new Date().toISOString(),
              createdBy: this.getCurrentUserId()
            }
            // 保存到后台（模拟）
            const resData = await this.saveQueryToBackend(queryData)
            if (resData.code === 0) {
              // 添加到保存的查询列表
              // this.savedQueries.push(queryData)

              // 如果设为默认，更新其他查询的默认状态
              // if (queryData.isDefault) {
              //   this.savedQueries.forEach(query => {
              //     if (query.id !== queryData.id) {
              //       query.isDefault = false
              //     }
              //   })
              // }
              // 加载保存的查询
              await this.loadSavedQueries()

              this.$message.success('查询条件保存成功')
              this.showSaveDialog = false
              this.resetSaveForm()
            } else {
              this.$message.error('保存失败：' + resData.msg)
            }
          } catch (error) {
            this.$message.error('保存失败：' + error.message)
          } finally {
            this.saving = false
          }
        }
      })
    },

    // 保存到后台（模拟）
    async saveQueryToBackend (queryData) {
      // 这里应该是实际的API调用
      return insertUserSelect(queryData)
      // return new Promise((resolve) => {
      //   setTimeout(() => {
      //     // 保存到localStorage（模拟后台）
      //     const saved = JSON.parse(localStorage.getItem('savedQueries') || '[]')
      //     saved.push(queryData)
      //     localStorage.setItem('savedQueries', JSON.stringify(saved))
      //     resolve()
      //   }, 500)
      // })
    },
    // 获取当前用户ID（模拟）
    getCurrentUserId () {
      return this.info.username + this.ModularName
    },
    // 加载保存的查询
    async loadSavedQueries () {
      this.animationLoading = true
      try {
        // 从后台加载（模拟）
        // const saved = JSON.parse(localStorage.getItem('savedQueries') || '[]')
        const saved = await getUserSelectList(Object.assign({ userId: this.info.username, menuId: this.ModularName }))

        // 如果是共享查询，需要根据权限过滤
        // const filteredQueries = saved.filter(query => {
        //   // 如果是自己的查询或者共享查询
        //   return query.createdBy === this.getCurrentUserId() || query.isShared
        // })
        //
        // this.savedQueries = filteredQueries
        this.savedQueries = saved.data

        // 如果有默认查询，应用它
        // const defaultQuery = this.savedQueries.find(q => q.isDefault)
        // if (defaultQuery) {
        //   this.applySavedQuery(defaultQuery)
        // }
      } catch (error) {
        console.error('加载保存的查询失败：', error)
      } finally {
        setTimeout(() => {
          this.animationLoading = false
        }, 500)
      }
    },
    // 应用时 把多选和日期参数值 转为数组类型
    transformSavedParams (params) {
      // 定义需要处理的 searchType 类型
      const selectTypes = ['selectopt', 'selectdict', 'selectmoreopt', 'selectmoredict']
      const dateTypes = ['datetimeclear', 'daterange', 'datetimerange']

      // 对每个参数进行处理
      return params.map(param => {
        // 创建参数的副本，避免修改原始数据
        const transformed = { ...param }

        // 检查是否需要转换 select 类型
        if (param.operator === 'between' && selectTypes.includes(param.searchType)) {
          if (param.value) {
            // 字符串转为数组，使用逗号分隔
            transformed.value = param.value.split(',')
          }
        }

        // 检查是否需要转换日期类型
        if (dateTypes.includes(param.searchType)) {
          if (param.value) {
            // 字符串转为日期范围，使用 ~ 分隔（可根据需求调整分隔符）
            transformed.value = param.value.split('~')
          }
        }

        return transformed
      })
    },
    // 应用保存的查询
    applySavedQuery (query) {
      // this.conditions = JSON.parse(JSON.stringify(query.detailsList))
      this.conditions = this.transformSavedParams(query.detailsList)
      this.currentQueryId = query.id

      // 如果查询条件为空，添加一个空条件
      if (this.conditions.length === 0) {
        this.conditions.push({
          prop: '',
          operator: '',
          value: '',
          searchType: '',
          logic: 'and'
        })
      }

      this.$message.success(`已应用查询：${query.selectName}`)
      this.$emit('apply-saved-query', query)
    },
    // 删除保存的查询
    async deleteSavedQuery (query) {
      try {
        await this.$confirm('确定删除“' + query.selectName + '”这个查询条件吗？', '提示', {
          type: 'warning'
        })
        // 从后台删除（模拟）
        await delUserSelect(query.id)
        // const saved = JSON.parse(localStorage.getItem('savedQueries') || '[]')
        // const newSaved = saved.filter(q => q.id !== queryId)
        // localStorage.setItem('savedQueries', JSON.stringify(newSaved))

        // 更新列表
        await this.loadSavedQueries()
        // this.savedQueries = newSaved.filter(query => {
        //   return query.createdBy === this.getCurrentUserId() || query.isShared
        // })

        // 如果删除的是当前查询，重置当前查询ID
        if (this.currentQueryId === query.id) {
          this.currentQueryId = null
        }

        this.$message.success('删除成功')
        this.$emit('delete-saved-query', query.id)
      } catch (error) {
        // 用户取消删除
      }
    },
    // 保存到本地存储（临时）
    saveToLocal (queryParams) {
      const history = JSON.parse(localStorage.getItem('searchHistory') || '[]')
      history.unshift({
        timestamp: new Date().toISOString(),
        params: queryParams
      })

      // 只保留最近的10条记录
      if (history.length > 10) {
        history.pop()
      }

      localStorage.setItem('searchHistory', JSON.stringify(history))
    },
    // 重置保存表单
    resetSaveForm () {
      this.saveForm = {
        name: '',
        isDefault: false,
        isShared: false
      }
      if (this.$refs.saveForm) {
        this.$refs.saveForm.resetFields()
      }
    },
    // 获取历史查询记录
    getHistoryQueries () {
      const history = JSON.parse(localStorage.getItem('searchHistory') || '[]')
      return history
    },
    // 清除所有查询条件
    clearAllConditions () {
      this.conditions = [
        {
          prop: '',
          operator: '',
          value: '',
          searchType: '',
          logic: 'and'
        }
      ]
    }
  }
}
</script>

<style lang="scss" scoped>
$--color-primary: #f8d02e;
$--color-error: #f56c6c;
$--color-success: #67c23a;
$--color-info: #409eff;
$--color-warning: #e6a23c;
$--color-purple: #7a63ea;
$--color-pink: #e86565;
.advanced-search {
  padding: 20px;
  //background-color: #f5f7fa;
  background-color: #FFFFFF;
  border-radius: 4px;
  //border: 1px solid #ebeef5;
  //border: 1px solid #F56C6C;
}

.saved-queries-section {
  margin-bottom: 20px;
  padding-bottom: 15px;
  //border-bottom: 1px dashed #dcdfe6;
  border-bottom: 1px dashed #DCDFE6;
}

.saved-queries-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  font-size: 14px;
  color: #606266;
}

.saved-queries-header span {
  font-weight: 600;
}

.saved-queries-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.query-tag {
  cursor: pointer;
  transition: all 0.3s;
}

.query-tag:hover {
  opacity: 0.8;
  transform: translateY(-2px);
}

.condition-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
}

.field-select {
  flex: 2;
  min-width: 180px;
}

.operator-select {
  flex: 1;
  min-width: 120px;
}

.value-input {
  flex: 2;
  min-width: 200px;
}

.number-range-input {
  display: flex;
  align-items: center;
  gap: 8px;
}

.range-input {
  flex: 1;
}

.range-separator {
  color: #909399;
  font-size: 12px;
}

.delete-btn, .add-btn {
  flex-shrink: 0;
}

.action-buttons {
  margin-top: 20px;
  text-align: center;
}

.tip-text {
  margin-left: 10px;
  color: #909399;
  font-size: 12px;
}

/* 逻辑关系选择器 */
.logic-select {
  width: 80px;
  margin-right: 10px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .condition-row {
    flex-wrap: wrap;
  }

  .field-select,
  .operator-select,
  .value-input {
    flex: 1 1 100%;
    margin-bottom: 10px;
  }

  .delete-btn,
  .add-btn {
    margin-left: auto;
  }
}

/* 动画效果 */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
::v-deep .saveSearchDialog {
  border-radius: 8px;
  .el-dialog__header {
    .el-dialog__title {
      font-size: 16px;
    }
  }
}
::v-deep .custom-primary-btn {
  background: $--color-primary; border-color: $--color-primary; color:#333;
  &.is-plain {
    color: #333;
    border-color: $--color-primary; background:fade-out($--color-primary, 0.9);
    &.is-disabled, &.is-disabled:active, &.is-disabled:hover, &.is-disabled:focus {
      color: #333;
      border-color: $--color-primary; background:fade-out($--color-primary, 0.9);
    }
  }
  &.is-disabled, &.is-disabled:active, &.is-disabled:hover, &.is-disabled:focus {
    background: $--color-primary; border-color: $--color-primary; color:#333; opacity:.5;
  }
  &:hover {
    opacity: .8;
  }
}
::v-deep .custom-purple-btn {
  background: $--color-purple; border-color: $--color-purple; color:#333;
  &.is-plain {
    color: #333;
    border-color: $--color-purple; background:fade-out($--color-purple, 0.9);
    &.is-disabled, &.is-disabled:active, &.is-disabled:hover, &.is-disabled:focus {
      color: #333;
      border-color: $--color-purple; background:fade-out($--color-purple, 0.9);
    }
  }
  &.is-disabled, &.is-disabled:active, &.is-disabled:hover, &.is-disabled:focus {
    background: $--color-purple; border-color: $--color-purple; color:#333; opacity:.5;
  }
  &:hover {
    opacity: .8;
  }
}
::v-deep .custom-pink-btn {
  background: $--color-pink; border-color: $--color-pink; color:#333;
  &.is-plain {
    color: #333;
    border-color: $--color-pink; background:fade-out($--color-pink, 0.9);
    &.is-disabled, &.is-disabled:active, &.is-disabled:hover, &.is-disabled:focus {
      color: #333;
      border-color: $--color-pink; background:fade-out($--color-pink, 0.9);
    }
  }
  &.is-disabled, &.is-disabled:active, &.is-disabled:hover, &.is-disabled:focus {
    background: $--color-pink; border-color: $--color-pink; color:#333; opacity:.5;
  }
  &:hover {
    opacity: .8;
  }
}
</style>
<style lang="scss">
.advanced-search {
  /* 加载动画 */
  .loaderAnimation {
    width: 80px;
    height: 50px;
    position: relative;
  }

  .loader-text {
    position: absolute;
    top: 0;
    padding: 0;
    margin: 0;
    color: #C8B6FF;
    animation: text_713 3.5s ease both infinite;
    font-size: .8rem;
    letter-spacing: 1px;
  }

  .load {
    background-color: #9A79FF;
    border-radius: 50px;
    display: block;
    height: 16px;
    width: 16px;
    bottom: 0;
    position: absolute;
    transform: translateX(64px);
    animation: loading_713 3.5s ease both infinite;
  }

  .load::before {
    position: absolute;
    content: "";
    width: 100%;
    height: 100%;
    background-color: #D1C2FF;
    border-radius: inherit;
    animation: loading2_713 3.5s ease both infinite;
  }

  @keyframes text_713 {
    0% {
      letter-spacing: 1px;
      transform: translateX(0px);
    }

    40% {
      letter-spacing: 2px;
      transform: translateX(26px);
    }

    80% {
      letter-spacing: 1px;
      transform: translateX(32px);
    }

    90% {
      letter-spacing: 2px;
      transform: translateX(0px);
    }

    100% {
      letter-spacing: 1px;
      transform: translateX(0px);
    }
  }

  @keyframes loading_713 {
    0% {
      width: 16px;
      transform: translateX(0px);
    }

    40% {
      width: 100%;
      transform: translateX(0px);
    }

    80% {
      width: 16px;
      transform: translateX(64px);
    }

    90% {
      width: 100%;
      transform: translateX(0px);
    }

    100% {
      width: 16px;
      transform: translateX(0px);
    }
  }

  @keyframes loading2_713 {
    0% {
      transform: translateX(0px);
      width: 16px;
    }

    40% {
      transform: translateX(0%);
      width: 80%;
    }

    80% {
      width: 100%;
      transform: translateX(0px);
    }

    90% {
      width: 80%;
      transform: translateX(15px);
    }

    100% {
      transform: translateX(0px);
      width: 16px;
    }
  }
}
</style>

<vue-filename-injector>
export default function (Component) {
  Component.options.__source = "src/components/d2-container-query/AdvancedSearch.vue"
}
</vue-filename-injector>
